import React, {useMemo, useState} from 'react'
import {
  AppBar,
  Box,
  Button,
  Dialog,
  Drawer,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonaPicker from './PersonaPicker'
import PreviewRunner from './PreviewRunner'
import RouteTracePanel from './RouteTracePanel'
import {toPreviewItems} from '../../utils/surveyItems'
import {projectForPersona} from '../../utils/skipLogic/preview'
import {explainRoute} from '../../utils/routing/resolveRoute'
import {answerMapFromItems, routeShownIndices} from './routeResolver'

const VIEWPORTS = {
  desktop: {label: 'Desktop', width: '100%'},
  mobile: {label: 'Mobile', width: 390},
}

const SKIP_LOGIC = 2

const itemLabel = item => {
  if (!item) return 'Item'
  if (item.kind === 1) return item.content?.headline?.trim() || 'Content screen'
  return (item.staffText || item.parentText || item.pupilText || item.text || '').trim() || `Question ${item.id}`
}

// Full-screen, no-write preview of a survey with skip logic. The author first
// chooses who to preview as (PersonaPicker), then walks the survey as that
// respondent (PreviewRunner) with a live route trace beside it. Nothing is ever
// submitted - the banner says so and there is no Submit. Exit returns to wherever
// the preview was opened from (onExit). Reached from the build and review screens.
//
// entries are the builder's ordered items (questions + content screens + skip-logic
// sections); they are re-keyed for the evaluator once, up front.
export default function SurveyPreview({
  open = true,
  entries,
  audience,
  language = 'en',
  surveyName,
  theme,
  onExit,
}) {
  const previewItems = useMemo(() => toPreviewItems(entries), [entries])
  const [persona, setPersona] = useState(null)
  const [responses, setResponses] = useState([])
  const [viewport, setViewport] = useState('desktop')
  const [traceOpen, setTraceOpen] = useState(false)
  const [visited, setVisited] = useState([0])
  const [done, setDone] = useState(false)
  const [jumped, setJumped] = useState(false)
  const [itemsOpen, setItemsOpen] = useState(false)

  // Projection depends only on the persona, so it is stable across answers.
  const {items, facts, factLabels} = useMemo(
    () =>
      persona
        ? projectForPersona(previewItems, persona)
        : {items: previewItems, facts: {}, factLabels: {}},
    [previewItems, persona]
  )

  const byId = {}
  responses.forEach(r => (byId[r.questionId] = r))
  const answerMap = answerMapFromItems(items, byId)
  const trace = persona ? explainRoute(items, answerMap, facts) : null

  const changePersona = () => {
    setResponses([])
    setVisited([0])
    setDone(false)
    setJumped(false)
    setPersona(null)
  }
  const start = chosen => {
    const chosenPersona = chosen || {}
    // Start on the first item actually shown, not raw index 0: a survey can open
    // with a skip-logic section, which is never a shown item.
    const {items: pItems, facts: pFacts} = projectForPersona(previewItems, chosenPersona)
    const shown = routeShownIndices(pItems, {}, pFacts)
    setResponses([])
    setVisited([shown.length ? shown[0] : 0])
    setDone(false)
    setJumped(false)
    setPersona(chosenPersona)
  }
  const onAnswer = value =>
    setResponses(rs => {
      const next = rs.filter(r => r.questionId !== value.questionId)
      next.push(value)
      return next
    })
  // Jumping goes straight to an item, ignoring routing; Back still returns here.
  const jumpTo = index => {
    setItemsOpen(false)
    setDone(false)
    setJumped(true)
    setVisited(v => [...v, index])
  }

  const frameWidth = VIEWPORTS[viewport].width

  return (
    <Dialog fullScreen open={open} onClose={onExit}>
      <AppBar position="relative" color="default" elevation={0} sx={{borderBottom: '1px solid #D7E3E8'}}>
        <Toolbar sx={{gap: 2}}>
          <Typography variant="subtitle1" sx={{flex: 1}}>
            Preview{surveyName ? ` — ${surveyName}` : ''}
          </Typography>
          {persona && (
            <>
              <Button size="small" onClick={() => setItemsOpen(true)}>
                Items
              </Button>
              <Stack direction="row" spacing={1}>
                {Object.entries(VIEWPORTS).map(([key, v]) => (
                  <Button
                    key={key}
                    size="small"
                    variant={viewport === key ? 'contained' : 'text'}
                    onClick={() => setViewport(key)}
                  >
                    {v.label}
                  </Button>
                ))}
              </Stack>
              <Link component="button" type="button" underline="hover" onClick={changePersona}>
                Change persona
              </Link>
            </>
          )}
          <Button startIcon={<CloseIcon />} onClick={onExit}>
            Exit
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        role="status"
        sx={{
          px: 3,
          py: 1,
          backgroundColor: '#FFF6E5',
          color: '#8A6D1B',
          borderBottom: '1px solid #F0E2BE',
          fontSize: 14,
        }}
      >
        Preview only — answers will not be saved.
      </Box>

      <Box sx={{display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden'}}>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: theme?.backgroundColor,
          }}
        >
          <Box sx={{width: frameWidth, maxWidth: '100%', p: 2}}>
            {persona ? (
              <PreviewRunner
                items={items}
                facts={facts}
                answerMap={answerMap}
                audience={audience}
                language={language}
                onAnswer={onAnswer}
                onRestart={() => {
                  setResponses([])
                  setJumped(false)
                }}
                visited={visited}
                onVisited={setVisited}
                done={done}
                onDone={setDone}
              />
            ) : (
              <PersonaPicker items={previewItems} onStart={start} />
            )}
          </Box>
        </Box>
        {persona && (
          <Box sx={{width: traceOpen ? 300 : 'auto', flexShrink: 0, overflow: 'auto'}}>
            <RouteTracePanel
              trace={trace}
              items={items}
              factLabels={factLabels}
              jumped={jumped}
              open={traceOpen}
              onToggle={() => setTraceOpen(o => !o)}
            />
          </Box>
        )}
      </Box>

      <Drawer anchor="left" open={itemsOpen} onClose={() => setItemsOpen(false)}>
        <Box sx={{width: 300, p: 2}}>
          <Typography variant="subtitle2" sx={{mb: 1}}>
            Items
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Jumping to an item bypasses skip-logic routing.
          </Typography>
          <List dense>
            {items.map((item, index) =>
              item.kind === SKIP_LOGIC ? null : (
                <ListItemButton key={item.id} onClick={() => jumpTo(index)}>
                  <ListItemText primary={itemLabel(item)} />
                </ListItemButton>
              )
            )}
          </List>
        </Box>
      </Drawer>
    </Dialog>
  )
}
